/**
 * Imports — the two file-based channels, and what each run did (FR-42).
 *
 * "Silent drops are not acceptable — if the channel ever stops working, this
 * summary is how you notice." So each run says how many lines it read, how many
 * it surfaced, and how many it set aside and why. The file checks themselves
 * (type, size, shape) are only spelled out when one of them failed.
 */
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { useApi, useAction } from '../hooks.js';
import { ErrorBox, Icon, Notice, PageHead, Pill, Skeleton, formatDate, toast } from '../ui.js';
import type { ImportRun } from '../types.js';

const CHANNELS = [
  { id: 'amazon', title: 'Amazon orders', cadence: 'Once a year', icon: 'cart' },
  { id: 'fidelity', title: 'Fidelity HSA activity', cadence: 'Every three months', icon: 'bank' },
];

const REASONS: Record<string, string> = {
  not_qualified: 'not medical',
  pre_establishment_date: 'before the HSA opened',
  already_imported: 'already imported',
  cancelled: 'cancelled',
  returned: 'returned',
  suppressed_asin: 'rejected before',
  zero_amount: 'no charge',
};

export function Imports() {
  const amazon = useApi<{ runs: ImportRun[] }>('/api/imports?channel=amazon');
  const fidelity = useApi<{ runs: ImportRun[] }>('/api/imports?channel=fidelity');
  const runs = { amazon, fidelity } as const;

  return (
    <>
      <PageHead title="Imports">Bring in expenses and account activity from a file you download.</PageHead>

      <div className="list">
        {CHANNELS.map((c) => (
          <Link className="item" to={`/wizards/${c.id}`} key={c.id}>
            <span className="tile">
              <Icon name={c.icon} />
            </span>
            <span>
              <div className="title">{c.title}</div>
              <div className="sub">{c.cadence} · step-by-step guide and upload</div>
            </span>
            <span className="end muted">
              <Icon name="chevron" />
            </span>
          </Link>
        ))}
      </div>

      {CHANNELS.map((c) => {
        const state = runs[c.id as keyof typeof runs];
        const list = state.data?.runs ?? [];
        if (state.error) return <ErrorBox key={c.id} error={state.error} />;
        if (state.loading) return <Skeleton key={c.id} rows={1} />;
        if (list.length === 0) return null;
        return (
          <section className="stack tight" key={c.id}>
            <h2 className="section">
              <span>{c.title} · past imports</span>
            </h2>
            {list.map((run) => (
              <Run key={run.run_id} run={run} />
            ))}
          </section>
        );
      })}

      <RejectedProducts />
    </>
  );
}

function Run({ run }: { run: ImportRun }) {
  const failed = run.validation && !run.validation.ok;
  const set = Object.entries(run.stats.rejected_by_reason);
  return (
    <div className="card stack tight">
      <div className="row-wrap" style={{ justifyContent: 'space-between' }}>
        <strong>{formatDate(run.created_at, { year: 'always' })}</strong>
        {failed ? (
          <Pill tone="stop">Not imported</Pill>
        ) : run.imported_at ? (
          <Pill tone="ok">Done</Pill>
        ) : run.validation ? (
          <Pill tone="warn">Ready to review</Pill>
        ) : (
          <Pill>Checking the file</Pill>
        )}
      </div>

      {failed ? (
        <Notice kind="stop" title="The file was not accepted, and nothing was imported">
          {run.validation!.checks
            .filter((check) => !check.ok)
            .map((check) => (
              <div key={check.name}>{check.detail}</div>
            ))}
        </Notice>
      ) : run.validation ? (
        <p className="small muted">
          {run.stats.rows_parsed.toLocaleString('en-US')} lines read, {run.stats.candidates} worth a look
          {set.length > 0
            ? `. Set aside: ${set.map(([reason, n]) => `${n.toLocaleString('en-US')} ${REASONS[reason] ?? reason.replace(/_/g, ' ')}`).join(', ')}.`
            : '.'}
        </p>
      ) : null}

      {run.validation && run.validation.gaps.length > 0 ? (
        <p className="small tone-warn">Gaps in what this file covers: {run.validation.gaps.join('; ')}</p>
      ) : null}

      {!failed ? (
        <div className="actions">
          <Link className={`btn${run.imported_at ? '' : ' primary'}`} to={`/imports/${run.run_id}`}>
            {run.imported_at ? 'Open' : 'Review'}
          </Link>
        </div>
      ) : null}
    </div>
  );
}

/**
 * FR-38 — a rejected product is remembered and skipped next year. That memory
 * has to be visible and reversible, or one mis-tap hides a product for good.
 */
function RejectedProducts() {
  const memory = useApi<{
    suppressed_products: { asin: string; product_name: string; rejected_at: string }[];
  }>('/api/asins');
  const restore = useAction(async (asin: string) => {
    await api.post('/api/asins/unsuppress', { asin });
    toast('Restored. It will be offered again on the next import.');
    memory.reload();
  });

  const suppressed = memory.data?.suppressed_products ?? [];
  if (suppressed.length === 0) return null;

  return (
    <details className="more">
      <summary>Amazon products you rejected · {suppressed.length}</summary>
      <div className="stack tight">
        <p className="xs muted">These are skipped on future imports. Restore one if it was rejected by mistake.</p>
        <div className="list">
          {suppressed.map((p) => (
            <div className="item plain" key={p.asin}>
              <span>
                <div className="title" style={{ fontWeight: 500 }}>
                  {p.product_name || p.asin}
                </div>
                <div className="sub">Rejected {formatDate(p.rejected_at, { year: 'always' })}</div>
              </span>
              <button className="quiet" onClick={() => void restore.run(p.asin)} disabled={restore.pending}>
                Restore
              </button>
            </div>
          ))}
        </div>
        {restore.error ? <ErrorBox error={restore.error} /> : null}
      </div>
    </details>
  );
}
